import { Button, Input, message, Modal, Select } from "antd";
import React, { useEffect } from "react";
import {
  useCreateInput,
  useInputById,
  useUpdateInput,
} from "../../api/input";
import { Controller, useForm } from "react-hook-form";
import { InputFormValues, inputSchema } from "../../schemas/formSchema";
import { zodResolver } from "@hookform/resolvers/zod";
import { InputProduct } from "../../enums/inputProduct.enum";
import { UnitOfMeasurementSelect } from "../form/UnitOfMeasurementSelect";

type InputModalProps = {
  open: boolean;
  selectedId: string;
  selectedInput?: any;
  onSubmit?: (data: any) => void;
  isInputCreatePending?: boolean;
  hideModal: () => void;
};

const InputModal: React.FC<InputModalProps> = ({
  open,
  selectedId,
  selectedInput,
  hideModal,
}) => {
  const { TextArea } = Input;

  const { mutateAsync: createInput, isPending: isInputCreatePending } =
    useCreateInput();
  const { mutateAsync: updateInput, isPending: isInputUpdatePending } =
    useUpdateInput();

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<InputFormValues>({
    resolver: zodResolver(inputSchema) as any,
    defaultValues: {
      category: undefined,
      name: "",
      unitOfMeasurementId: undefined,
      description: "",
    },
  });

  const { data: input, isLoading: isLoadingInput } = useInputById(selectedId);

  useEffect(() => {
    if (!open) return;

    if (selectedId && input?.data?.data) {
      reset({
        category: input.data.data.category || undefined,
        name: input.data.data.name || "",
        unitOfMeasurementId:
          input.data.data.unitOfMeasurementId || undefined,
        description: input.data.data.description || "",
      });
      return;
    }

    if (selectedInput) {
      reset({
        category: selectedInput.category || undefined,
        name: selectedInput.name || "",
        unitOfMeasurementId: selectedInput.unitOfMeasurementId || undefined,
        description: selectedInput.description || "",
      });
      return;
    }

    reset({
      category: undefined,
      name: "",
      unitOfMeasurementId: undefined,
      description: "",
    });
  }, [selectedInput, selectedId, input, reset, open]);

  const inputProducts = Object.entries(InputProduct).map(([key, value]) => ({
    value,
    label: key.replace(/_/g, " "),
  }));

  const onSubmit = async (data: InputFormValues) => {
    if (!data) return;

    const payload = {
      category: data.category,
      name: data.name,
      unitOfMeasurementId: data.unitOfMeasurementId,
      description: data.description ?? "",
    };

    try {
      if (selectedId) {
        const result = await updateInput({
          id: Number(selectedId),
          input: payload,
        });

        if (result?.id) {
          message.success("Input product updated successfully");
          reset();
        }
      } else {
        const result = await createInput(payload);

        if (result?.data?.data?.id) {
          message.success("Input product created successfully");
          reset();
        }
      }

      hideModal();
    } catch {
      message.error("Operation failed");
    }
  };

  return (
    <Modal
      title={
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 600,
            fontSize: 27.43,
            color: "black",
          }}
        >
          {selectedId ? "Edit input product" : "Create input product"}
        </div>
      }
      open={open}
      centered
      onOk={handleSubmit(onSubmit)}
      onCancel={hideModal}
      closable
      confirmLoading={isLoadingInput}
      footer={
        <div className="flex justify-end gap-3 p-4">
          <Button
            onClick={hideModal}
            className="!border-gray-300 !text-gray-700 font-medium hover:!bg-gray-100 hover:!text-gray-900 transition-all duration-200 bg-transparent"
          >
            Cancel
          </Button>

          <Button
            type="primary"
            onClick={handleSubmit(onSubmit)}
            className="!bg-[#009602] !border-none hover:!bg-[#00b306] font-medium transition-all duration-200"
            loading={isInputCreatePending || isInputUpdatePending}
          >
            {selectedId ? "Update" : "Create"}
          </Button>
        </div>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Category *
          </label>
          <Controller
            name="category"
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                placeholder="Select category"
                style={{ width: "100%" }}
                options={inputProducts.map(
                  (product: { value: InputProduct; label: string }) => ({
                    label: product.label,
                    value: product.value,
                  }),
                )}
              />
            )}
          />
          {errors.category && (
            <div className="text-red-400">{errors.category.message}</div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Name *
          </label>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <Input {...field} placeholder="Insert name" />
            )}
          />
          {errors.name && (
            <div className="text-red-400">{errors.name.message}</div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Unit of measurement *
          </label>
          <Controller
            name="unitOfMeasurementId"
            control={control}
            render={({ field }) => (
              <UnitOfMeasurementSelect
                value={field.value}
                onChange={field.onChange}
                status={errors.unitOfMeasurementId ? "error" : undefined}
              />
            )}
          />
          {errors.unitOfMeasurementId && (
            <div className="text-red-400">
              {errors.unitOfMeasurementId.message}
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <TextArea {...field} rows={4} placeholder="Description" />
            )}
          />
        </div>
      </div>
    </Modal>
  );
};

export default InputModal;
